import { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { PromptPowerUp } from '../../components/PromptPowerUp';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { Image, Sparkles, Download, Save, RefreshCw, Upload, X, Zap, Crown } from 'lucide-react';
import { API, GenerationProgress, downloadFile } from './shared';
import { useIdempotencyKey } from '../../lib/useIdempotencyKey';
import { useStudioErrorHandler } from '../../components/SubmissionUncertainModal';
import { useHubPricing, formatCreditRange } from '../../lib/useHubPricing';
import { useStudioHealth, StudioHealthBanner } from '../../lib/useStudioHealth';

const ASPECT_RATIOS = ['1:1', '16:9', '9:16', '4:3', '3:4'];

export function ImageStudio({ onSave }) {
  const [prompt, setPrompt] = useState('');
  const [aspect, setAspect] = useState('1:1');
  const [tier, setTier] = useState('standard');
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMsg, setProgressMsg] = useState('');
  const [result, setResult] = useState(null);
  const [refUrls, setRefUrls] = useState([]);
  const [uploadQueue, setUploadQueue] = useState(0); // live count of uploading placeholders
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [dragIndex, setDragIndex] = useState(null); // for thumbnail reorder
  const refFileInputRef = useRef(null);
  const studioRef = useRef(null);
  const maxRefs = tier === 'pro' ? 14 : 8;

  /**
   * Central file ingestion. Used by file picker, drag-drop, and clipboard paste.
   * Validates per-file, runs uploads sequentially so each placeholder resolves
   * in order, and surfaces a precise toast for every skipped file.
   */
  const ingestFiles = useCallback(async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;

    // Snapshot current slot count without triggering side effects inside the
    // state updater (must be pure). We use the functional getter pattern but
    // we DO NOT kick off uploads in there — that runs after.
    let queue = [];
    setRefUrls(prev => {
      const slotsLeft = maxRefs - prev.length;
      if (slotsLeft <= 0) {
        toast.info(`You've reached the ${maxRefs}-reference cap for this tier.`);
        queue = [];
        return prev;
      }
      queue = list.slice(0, slotsLeft);
      if (list.length > slotsLeft) {
        toast.info(`Only ${slotsLeft} more reference${slotsLeft === 1 ? '' : 's'} fit in this tier.`);
      }
      return prev;
    });

    // Allow React to flush the (no-op) state update before kicking off uploads.
    await Promise.resolve();
    if (!queue.length) return;

    setUploadQueue(q => q + queue.length);
    for (const file of queue) {
      try {
        if (!file.type || !file.type.startsWith('image/')) {
          toast.error(`${file.name || 'file'} skipped — not an image`);
          setUploadQueue(q => Math.max(0, q - 1));
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} skipped — over 10MB`);
          setUploadQueue(q => Math.max(0, q - 1));
          continue;
        }
        const fd = new FormData();
        fd.append('file', file);
        const { data } = await axios.post(`${API}/api/creative-studio/upload/image`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data?.url) setRefUrls(current => [...current, data.url]);
        else toast.error(`${file.name}: upload returned no URL`);
      } catch (err) {
        const msg = err?.response?.data?.detail || err?.message || 'upload failed';
        toast.error(`${file.name || 'file'}: ${msg}`);
      } finally {
        setUploadQueue(q => Math.max(0, q - 1));
      }
    }
  }, [maxRefs]);

  const handleRefUpload = (e) => {
    const files = e.target.files;
    e.target.value = '';
    ingestFiles(files);
  };

  // Drag-and-drop: whole reference row accepts drops.
  const handleDragOver = useCallback((e) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  }, []);
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only leave state when pointer exits the wrapper (not when it enters a child).
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDraggingOver(false);
  }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    const files = e.dataTransfer?.files;
    if (files && files.length) ingestFiles(files);
  }, [ingestFiles]);

  // Clipboard paste — anywhere inside the studio. Skips paste events that
  // originate from the Prompt textarea (user typing, not pasting images).
  useEffect(() => {
    const el = studioRef.current;
    if (!el) return;
    const handler = (e) => {
      const items = e.clipboardData?.items;
      if (!items || !items.length) return;
      const imageFiles = [];
      for (const item of items) {
        if (item.kind === 'file' && item.type?.startsWith('image/')) {
          const f = item.getAsFile();
          if (f) imageFiles.push(f);
        }
      }
      if (imageFiles.length) {
        e.preventDefault();
        ingestFiles(imageFiles);
      }
    };
    el.addEventListener('paste', handler);
    return () => el.removeEventListener('paste', handler);
  }, [ingestFiles]);

  // Reorder thumbnails via HTML5 drag API.
  const handleThumbDragStart = (i) => () => setDragIndex(i);
  const handleThumbDragOver = (i) => (e) => {
    if (dragIndex === null || dragIndex === i) return;
    e.preventDefault();
  };
  const handleThumbDrop = (i) => (e) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === i) { setDragIndex(null); return; }
    setRefUrls(prev => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(i, 0, moved);
      return next;
    });
    setDragIndex(null);
  };
  const handleThumbDragEnd = () => setDragIndex(null);

  // v3.0 Kaharian: stable Idempotency-Key + uncertain-submission handler
  const idempotencyKey = useIdempotencyKey('konnect-image', [prompt, aspect, tier, refUrls.join(',')]);
  const { handleStudioError, SubmissionUncertainDialog } = useStudioErrorHandler();
  const { getPrice, isLoading: pricingLoading } = useHubPricing();
  const imagePriceRow = getPrice('IMAGE.generate');

  // v3.2: shared provider-health probe + auto-retry
  const { health, cooldown, onProvidersUnavailable, pendingRetryRef } = useStudioHealth(
    'IMAGE',
    { onRecover: () => generate() }
  );

  const pollRef = useRef(null);

  const generate = async () => {
    if (!prompt.trim()) { toast.error('Enter a prompt'); return; }
    setGenerating(true); setResult(null); setProgress(0);
    setProgressMsg('Preparing your request...');
    const progressInterval = setInterval(() => {
      setProgress(p => { if (p >= 90) { clearInterval(progressInterval); return 90; } return p + Math.random() * 8; });
      setProgressMsg(['Analyzing prompt...', 'Generating composition...', 'Rendering details...', 'Applying style...', 'Finalizing image...'][Math.floor(Math.random() * 5)]);
    }, 1500);
    try {
      const res = await axios.post(`${API}/api/creative-studio/generate/image`, {
        prompt: prompt.trim(), aspect_ratio: aspect, tier, reference_urls: refUrls.filter(Boolean),
        idempotency_key: idempotencyKey,
      });
      // Sync success — URL already available.
      if (res.data?.url) {
        setProgress(100); setProgressMsg('Done!');
        setTimeout(() => { setResult(res.data); setGenerating(false); }, 500);
        return;
      }
      // Async — Hub returned task_id; poll /task-status until ready.
      if (res.data?.task_id && res.data?.status === 'processing') {
        setProgressMsg('Provider is busy — keeping your spot in queue…');
        const deadline = Date.now() + 4 * 60 * 1000; // 4 min budget
        pollRef.current = setInterval(async () => {
          if (Date.now() > deadline) {
            clearInterval(pollRef.current); clearInterval(progressInterval);
            toast.error('Image is taking longer than expected. Check your Library in a minute — no credits charged until ready.');
            setGenerating(false);
            return;
          }
          try {
            const poll = await axios.get(`${API}/api/creative-studio/task-status/${res.data.task_id}`);
            if (poll.data.progress > 0) setProgress(Math.min(poll.data.progress, 95));
            if ((poll.data.status === 'completed' || poll.data.status === 'ready') && poll.data.url) {
              clearInterval(pollRef.current); clearInterval(progressInterval);
              setProgress(100); setProgressMsg('Done!');
              setTimeout(() => { setResult({ ...res.data, url: poll.data.url, status: 'completed' }); setGenerating(false); }, 500);
            } else if (poll.data.status === 'failed' || poll.data.status === 'error') {
              clearInterval(pollRef.current); clearInterval(progressInterval);
              toast.error('Image generation failed at the provider. No credits charged.');
              setGenerating(false);
            }
          } catch { /* keep polling through transient errors */ }
        }, 4000);
        return;
      }
      // No URL and no task_id — genuine failure.
      toast.error('Image generation returned no result. Please try again.');
      setGenerating(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const isProvidersDown = err?.response?.status === 503 && typeof detail?.code === 'string' && detail.code.endsWith('_PROVIDERS_UNAVAILABLE');
      if (isProvidersDown) {
        onProvidersUnavailable(detail, { armRetry: true });
        toast.error(`${detail.message || 'Temporarily unavailable'} · Auto-retrying in 30s…`, { duration: 5000, id: 'image-providers-down' });
      } else {
        handleStudioError(err, { category: 'IMAGE', idempotencyKey, fallbackMessage: 'Image generation failed' });
      }
      setGenerating(false);
    } finally { clearInterval(progressInterval); }
  };

  // Cleanup poll interval on unmount.
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const handleSave = () => {
    if (!result?.url) return;
    onSave({ type: 'image', prompt: result.prompt, url: result.url, tier: result.tier,
      aspect_ratio: result.aspect_ratio, credits_used: result.credits_used, title: result.prompt });
  };

  return (
    <div ref={studioRef} className="grid grid-cols-1 lg:grid-cols-5 gap-4" data-testid="image-studio" tabIndex={-1}>
      <SubmissionUncertainDialog />
      <div className="lg:col-span-5">
        <StudioHealthBanner category="IMAGE" health={health} cooldown={cooldown} pendingRetry={pendingRetryRef.current} />
      </div>
      <div className="lg:col-span-2 space-y-4">
        <Card className="bg-white border-gray-border">
          <CardContent className="pt-4 space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => setTier('standard')} className={cn("p-3 rounded-xl border-2 text-left transition-all",
                tier === 'standard' ? "border-amber-400 bg-amber-50" : "border-gray-border hover:border-gray-300")} data-testid="tier-standard">
                <Zap className="h-4 w-4 text-amber-500 mb-1" /><p className="text-xs font-bold">Standard</p>
                <p className="text-[10px] text-gray-medium">{pricingLoading ? '…' : formatCreditRange(imagePriceRow)} · Up to 8 refs</p>
              </button>
              <button onClick={() => setTier('pro')} className={cn("p-3 rounded-xl border-2 text-left transition-all",
                tier === 'pro' ? "border-purple-400 bg-purple-50" : "border-gray-border hover:border-gray-300")} data-testid="tier-pro">
                <Crown className="h-4 w-4 text-purple-500 mb-1" /><p className="text-xs font-bold">PRO</p>
                <p className="text-[10px] text-gray-medium">Higher quality · Up to 14 refs</p>
              </button>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs font-medium">Reference Images (optional)</Label>
                {refUrls.length >= 2 && (
                  <button
                    type="button"
                    onClick={() => setRefUrls([])}
                    className="text-[10px] text-gray-500 hover:text-red-500 transition-colors"
                    data-testid="clear-refs"
                  >
                    Clear all
                  </button>
                )}
              </div>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={cn(
                  "flex flex-wrap gap-2 mt-1 p-1 rounded-lg transition-all",
                  isDraggingOver && "ring-2 ring-purple-400 bg-purple-50/60"
                )}
                data-testid="ref-dropzone"
              >
                {refUrls.map((u, i) => (
                  <div
                    key={`${u}-${i}`}
                    draggable
                    onDragStart={handleThumbDragStart(i)}
                    onDragOver={handleThumbDragOver(i)}
                    onDrop={handleThumbDrop(i)}
                    onDragEnd={handleThumbDragEnd}
                    className={cn(
                      "relative w-16 h-16 rounded-lg border border-gray-border overflow-hidden bg-gray-50 cursor-grab active:cursor-grabbing transition-all",
                      dragIndex === i && "opacity-40 scale-95",
                      i === 0 && "ring-1 ring-amber-400"
                    )}
                    title={i === 0 ? 'Primary reference (drag to reorder)' : 'Drag to reorder'}
                    data-testid={`ref-thumb-${i}`}
                  >
                    <img src={u} alt="" className="w-full h-full object-cover pointer-events-none" onError={e => e.target.src = ''} />
                    <button onClick={() => setRefUrls(refUrls.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center z-10">
                      <X className="h-2.5 w-2.5 text-white" />
                    </button>
                  </div>
                ))}
                {/* Uploading placeholders — one per in-flight file */}
                {Array.from({ length: uploadQueue }).map((_, i) => (
                  <div
                    key={`uploading-${i}`}
                    className="w-16 h-16 rounded-lg border border-gray-border bg-gray-50 flex items-center justify-center"
                    data-testid="ref-uploading"
                  >
                    <RefreshCw className="h-4 w-4 text-gray-400 animate-spin" />
                  </div>
                ))}
                {refUrls.length + uploadQueue < maxRefs && (
                  <>
                    <input
                      ref={refFileInputRef}
                      id="ref-image-upload-input"
                      type="file"
                      accept="image/*"
                      multiple
                      className="sr-only"
                      onChange={handleRefUpload}
                      data-testid="add-ref-image-input"
                    />
                    {/* Native <label htmlFor> — works on iOS Safari, Android Chrome,
                        Samsung Internet, WebViews. JS-triggered .click() on hidden
                        inputs is unreliable on mobile. */}
                    <label
                      htmlFor="ref-image-upload-input"
                      className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center hover:border-purple-400 hover:bg-purple-50/50 transition-colors cursor-pointer touch-manipulation"
                      data-testid="add-ref-image"
                      title="Click, drag & drop, or paste (Ctrl/⌘+V)"
                    >
                      <Upload className="h-4 w-4 text-gray-400" />
                      <span className="text-[8px] text-gray-400 mt-0.5">{refUrls.length + uploadQueue}/{maxRefs}</span>
                    </label>
                  </>
                )}
                {isDraggingOver && refUrls.length === 0 && uploadQueue === 0 && (
                  <div className="flex-1 min-w-[120px] h-16 rounded-lg border-2 border-dashed border-purple-300 flex items-center justify-center text-[10px] text-purple-600 font-medium pointer-events-none">
                    Drop images here
                  </div>
                )}
              </div>
              <p className="text-[10px] text-gray-400 mt-1 leading-snug">
                Click +, drag & drop files, or paste (Ctrl/⌘+V). First image = primary reference — drag to reorder.
              </p>
            </div>
            <div>
              <Label className="text-xs font-medium">Prompt</Label>
              <PromptPowerUp contextType="general">
              <Textarea placeholder="Describe the image you want to generate..." value={prompt} onChange={e => setPrompt(e.target.value)}
                className="mt-1 min-h-[100px] text-sm" data-testid="image-prompt" />
              </PromptPowerUp>
            </div>
            <div>
              <Label className="text-xs font-medium">Aspect Ratio</Label>
              <div className="flex gap-1.5 mt-1">
                {ASPECT_RATIOS.map(ar => (
                  <button key={ar} onClick={() => setAspect(ar)} className={cn("px-3 py-1.5 text-xs rounded-lg border transition-all font-medium",
                    aspect === ar ? "bg-text-primary text-white border-text-primary" : "border-gray-border hover:border-gray-300")} data-testid={`aspect-${ar.replace(':', '-')}`}>{ar}</button>
                ))}
              </div>
            </div>
            <Button className="w-full h-11" onClick={generate} disabled={generating || !prompt.trim() || pricingLoading || !imagePriceRow || !health.available} data-testid="generate-image-btn">
              <Sparkles className="h-4 w-4 mr-2" />{!health.available ? `Unavailable${cooldown > 0 ? ` · ${cooldown}s` : ''}` : (!pricingLoading && !imagePriceRow ? 'Unavailable' : 'Generate Image')}
            </Button>
          </CardContent>
        </Card>
      </div>
      <div className="lg:col-span-3 relative">
        <GenerationProgress active={generating} progress={Math.round(progress)} message={progressMsg} />
        {result?.url ? (
          <Card className="bg-white border-gray-border overflow-hidden" data-testid="image-result">
            <img src={result.url} alt="Generated" className="w-full object-contain max-h-[500px]" />
            <CardContent className="p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">AI Generated</Badge>
                  {result.credits_used > 0 && <Badge className="bg-amber-100 text-amber-700 text-[10px]">{result.credits_used} credits used</Badge>}
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" className="flex-1" onClick={handleSave} data-testid="save-to-library-btn"><Save className="h-3.5 w-3.5 mr-1" />Save to Library</Button>
                <Button size="sm" variant="outline" onClick={() => downloadFile(result.url, `konnect_image_${Date.now()}.png`)} data-testid="download-image-btn"><Download className="h-3.5 w-3.5 mr-1" />Download</Button>
                <Button size="sm" variant="ghost" onClick={() => setResult(null)} data-testid="regenerate-btn"><RefreshCw className="h-3.5 w-3.5" /></Button>
              </div>
            </CardContent>
          </Card>
        ) : !generating ? (
          <Card className="bg-white border-gray-border"><CardContent className="p-12 text-center">
            <Image className="h-16 w-16 mx-auto mb-4 text-gray-200" />
            <p className="text-sm text-gray-medium">Your generated image will appear here</p>
          </CardContent></Card>
        ) : null}
      </div>
    </div>
  );
}
